import { z } from 'zod';

/**
 * External test-suite ingest contract — the provider-agnostic report shape that
 * every producer (our hosted runner now; a customer's CI later) emits, and that
 * the ingest API validates. Kept deliberately small: raw per-test results +
 * a normalized step timeline + artifact references. The durable per-test
 * catalog, flaky-vs-fail classification, and read-model projection are computed
 * server-side from these (see the design doc). Storage lives in
 * `packages/db/src/schema/external-test.ts`.
 */

export const EXTERNAL_TEST_PROVIDERS = ['selenium', 'playwright', 'cypress', 'junit'] as const;
export type ExternalTestProvider = (typeof EXTERNAL_TEST_PROVIDERS)[number];

/**
 * Per-attempt wall-clock cap applied when a runner sets no `shardTimeoutMs`.
 *
 * Lives here rather than in the API because the config UI has to *name* the
 * resolved value — a bare `0` in a timeout field reads as "no limit", which is
 * the opposite of what it means. Two copies of this number would drift, and the
 * drift would surface only as a suite killed earlier than its own settings page
 * claimed. `run.job.ts` re-exports it, so API callers are unchanged.
 */
export const DEFAULT_SHARD_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Ceiling on a runner's configured `shardTimeoutMs`. Policy: a slice that cannot
 * finish in two hours wants more shards, not more patience.
 */
export const MAX_SHARD_TIMEOUT_MS = 2 * 60 * 60 * 1000;

/** Raw per-attempt result status as reported by the framework. */
export const EXTERNAL_TEST_RESULT_STATUSES = ['passed', 'failed', 'skipped'] as const;
export type ExternalTestResultStatus = (typeof EXTERNAL_TEST_RESULT_STATUSES)[number];

/** Coarse category for a normalized step (drives icons/grouping in the UI). */
export const EXTERNAL_TEST_STEP_CATEGORIES = [
  'navigate',
  'click',
  'type',
  'assert',
  'api',
  'other',
] as const;
export type ExternalTestStepCategory = (typeof EXTERNAL_TEST_STEP_CATEGORIES)[number];

export const EXTERNAL_TEST_ARTIFACT_KINDS = [
  'report',
  'video',
  'trace',
  'log',
  'screenshot',
] as const;
export type ExternalTestArtifactKind = (typeof EXTERNAL_TEST_ARTIFACT_KINDS)[number];

/** One structured error from a failing test. */
export const ExternalTestErrorSchema = z.object({
  message: z.string().optional(),
  stack: z.string().optional(),
  type: z.string().optional(),
});
export type ExternalTestError = z.infer<typeof ExternalTestErrorSchema>;

/** A normalized step within a test — powers the left-nav timeline + video scrub. */
export const ExternalTestStepSchema = z.object({
  index: z.number().int().nonnegative(),
  title: z.string(),
  status: z.enum(EXTERNAL_TEST_RESULT_STATUSES),
  category: z.enum(EXTERNAL_TEST_STEP_CATEGORIES).default('other'),
  startMs: z.number().nonnegative().optional(),
  durationMs: z.number().nonnegative().optional(),
  /** Reference (storage key) to a screenshot captured at this step. */
  screenshotRef: z.string().optional(),
  /** Reference (storage key) to a DOM snapshot captured at this step. */
  domRef: z.string().optional(),
});
export type ExternalTestStep = z.infer<typeof ExternalTestStepSchema>;

/** One test's result within a shard (one attempt). */
export const ExternalTestResultSchema = z.object({
  /**
   * Provider-immutable stable identity (repo + normalized spec path + describe/it
   * title path). NOT `file#title`. Used to attribute history to the durable test.
   */
  testKey: z.string().min(1),
  displayName: z.string().min(1),
  /** describe → it hierarchy (suite → section → scenario → action). */
  suitePath: z.array(z.string()).default([]),
  status: z.enum(EXTERNAL_TEST_RESULT_STATUSES),
  durationMs: z.number().nonnegative().optional(),
  /** 1-based attempt index (retries append). */
  attempt: z.number().int().positive().default(1),
  error: ExternalTestErrorSchema.optional(),
  steps: z.array(ExternalTestStepSchema).default([]),
});
export type ExternalTestResult = z.infer<typeof ExternalTestResultSchema>;

/** Reference to an uploaded artifact (bytes are PUT to a presigned URL first). */
export const ExternalTestArtifactRefSchema = z.object({
  kind: z.enum(EXTERNAL_TEST_ARTIFACT_KINDS),
  storageKey: z.string().min(1),
  /** Optional link to the specific test this artifact belongs to. */
  testKey: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
});
export type ExternalTestArtifactRef = z.infer<typeof ExternalTestArtifactRefSchema>;

/** Manifest posted when a run (or a shard within it) opens. */
export const ExternalTestRunManifestSchema = z.object({
  runnerId: z.string().uuid(),
  provider: z.enum(EXTERNAL_TEST_PROVIDERS),
  /** The framework/CI-side run id, if any (used for idempotent grouping). */
  providerRunId: z.string().optional(),
  commit: z.string().optional(),
  branch: z.string().optional(),
  /** Pinned commit SHA the shards ran against. */
  repoRevision: z.string().optional(),
  targetUrl: z.string().url().optional(),
  shardIndex: z.number().int().nonnegative().optional(),
  shardTotal: z.number().int().positive().optional(),
  startedAt: z.string().datetime().optional(),
});
export type ExternalTestRunManifest = z.infer<typeof ExternalTestRunManifestSchema>;

/** Body for `POST /api/v1/external-test-runs/:id/results` (idempotent append). */
export const ExternalTestResultsBatchSchema = z.object({
  shardIndex: z.number().int().nonnegative().optional(),
  results: z.array(ExternalTestResultSchema).min(1),
  artifacts: z.array(ExternalTestArtifactRefSchema).default([]),
});
export type ExternalTestResultsBatch = z.infer<typeof ExternalTestResultsBatchSchema>;

/**
 * Cross-attempt verdict computed server-side at seal. `flaky` = failed at least
 * one attempt but passed a later one; `failed` = failed every attempt
 * (definitive — the only verdict that triggers triage).
 */
export const EXTERNAL_TEST_VERDICTS = ['passed', 'flaky', 'failed', 'skipped'] as const;
export type ExternalTestVerdict = (typeof EXTERNAL_TEST_VERDICTS)[number];

/** Rolled-up reliability of a durable test over its history. */
export const EXTERNAL_TEST_HEALTHS = ['unverified', 'passing', 'flaky', 'failing'] as const;
export type ExternalTestHealth = (typeof EXTERNAL_TEST_HEALTHS)[number];
